import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../config/axiosInstance';
import { useLanguage } from '../../../hooks/useLanguage';
import './search.scss';

const Search = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const { t } = useTranslation();
    const { currentLang } = useLanguage();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (!query.trim()) {
            setResults(null);
            return;
        }

        const fetchResults = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance.get(`/search`, {
                    params: { q: query, lang: currentLang }
                });
                
                if (response.data && response.data.success) {
                    setResults(response.data.data);
                } else {
                    setResults({ totalResults: 0, results: {} });
                }
            } catch (err) {
                console.error("Search API error:", err);
                setError(t('search.error', 'Đã xảy ra lỗi khi tìm kiếm.'));
                setResults({ totalResults: 0, results: {} });
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [query, currentLang, t]);

    const handleItemClick = (item) => {
        if (item.type === 'customer' && item.slug && item.slug !== '#') {
            window.open(item.slug, '_blank');
        } else if (item.type === 'project' && item.slug) {
            navigate(`/du-an-tieu-bieu/${item.slug}`);
        } else if (item.type === 'news' && item.slug) {
            navigate(`/tin-tuc/${item.slug}`);
        } else if (item.type === 'ecosystem' && item.slug) {
            navigate(`/he-sinh-thai/${item.slug}`);
        } else if (item.type === 'product' && item.slug && item.groupSlug) {
            navigate(`/san-pham-giai-phap/${item.groupSlug}/${item.slug}`);
        }
    };

    const renderGroup = (title, items) => {
        if (!items || items.length === 0) return null;
        return (
            <div className="search-group">
                <h3>{title} ({items.length})</h3>
                <div className="search-grid">
                    {items.map(item => (
                        <div 
                            key={`${item.type}-${item.id}`} 
                            className={`search-card search-card--${item.type}`}
                            onClick={() => handleItemClick(item)}
                            role="button"
                            tabIndex={0}
                        >
                            <div className="search-card__image">
                                {item.thumbnail ? (
                                    <img src={item.thumbnail} alt={item.title} />
                                ) : (
                                    <div className="search-card__placeholder">No image</div>
                                )}
                            </div>
                            <div className="search-card__content">
                                <h4>{item.title}</h4>
                                {item.excerpt && item.excerpt !== item.title && (
                                    <p>
                                        {(() => {
                                            const stripHtml = (html) => {
                                                if (!html) return '';
                                                const doc = new DOMParser().parseFromString(html, 'text/html');
                                                return doc.body.textContent || "";
                                            };
                                            const cleanText = stripHtml(item.excerpt);
                                            return cleanText.length > 80 ? cleanText.substring(0, 80) + '...' : cleanText;
                                        })()}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className="search-page">
            <div className="layout_container">
                <div className="search-page__header">
                    <h2>{t('search.results_for', 'Kết quả tìm kiếm cho:')} <span>"{query}"</span></h2>
                </div>
                
                <div className="search-page__content">
                    {loading ? (
                        <div className="search-page__loading">
                            <div className="spinner"></div>
                            <p>{t('search.searching', 'Đang tìm kiếm...')}</p>
                        </div>
                    ) : error ? (
                        <div className="search-page__error">
                            <p>{error}</p>
                        </div>
                    ) : results && results.totalResults > 0 ? (
                        <div className="search-page__results">
                            {renderGroup(t('search.customers', 'Khách hàng / Đối tác'), results.results.customers)}
                            {renderGroup(t('search.projects', 'Dự án'), results.results.projects)}
                            {renderGroup(t('search.products', 'Sản phẩm & Giải pháp'), results.results.products)}
                            {renderGroup(t('search.ecosystem', 'Hệ sinh thái'), results.results.ecosystems)}
                            {renderGroup(t('search.news', 'Tin tức'), results.results.news)}
                        </div>
                    ) : query.trim() ? (
                        <div className="search-page__empty">
                            <div className="empty-animation">
                                <div className="empty-animation__circle"></div>
                                <div className="empty-animation__icon">🔍</div>
                            </div>
                            <h3>{t('search.no_results_title', 'Chưa có kết quả')}</h3>
                            <div className="search-page__empty-text">
                                <p>
                                    {t('search.no_results_desc1', 'Không tìm thấy nội dung nào khớp với từ khóa')} 
                                    <span className="keyword"> "{query}"</span>.
                                </p>
                                <p>{t('search.no_results_desc2', 'Vui lòng thử tìm kiếm với từ khóa khác.')}</p>
                            </div>
                            <button className="btn-back-home" onClick={() => navigate('/')}>
                                {t('common.home', 'Trang chủ')}
                            </button>
                        </div>
                    ) : (
                        <div className="search-page__empty">
                            <div className="empty-animation">
                                <div className="empty-animation__circle"></div>
                                <div className="empty-animation__icon">🔍</div>
                            </div>
                            <h3>{t('search.empty_title', 'Vui lòng nhập từ khóa')}</h3>
                            <div className="search-page__empty-text">
                                <p>{t('search.empty_desc', 'Nhập từ khóa vào ô tìm kiếm để bắt đầu.')}</p>
                            </div>
                            <button className="btn-back-home" onClick={() => navigate('/')}>
                                {t('common.home', 'Trang chủ')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Search;
